import {
  bytesLookLikePdf,
  contentLooksLikePdfSource,
  ORGANIZED_TOUR_PDF_DATA_URL_RE,
} from "./organizedTourContentDetect";
import {
  getOrgTourInfoDangerousInnerHtml,
  getOrgTourInfoPlainTextForDisplay,
  orgTourInfoContentLooksLikeRawSource,
  resolveOrgTourInfoDisplay,
} from "./orgTourInfoDisplay";

export { contentLooksLikePdfSource, bytesLookLikePdf } from "./organizedTourContentDetect";

export const ORG_TOUR_ITINERARY_PDF_PLACEHOLDER_HE = "כאן יוצג מסלול הטיול";

const PDF_DATA_URL_RE = ORGANIZED_TOUR_PDF_DATA_URL_RE;
const HTTP_URL_RE = /^https?:\/\//i;

function isHttpUrl(s) {
  return typeof s === "string" && HTTP_URL_RE.test(s.trim());
}

function urlLooksLikePdf(url) {
  const u = String(url || "").trim();
  if (!u) return false;
  try {
    const path = new URL(u).pathname.toLowerCase();
    if (path.endsWith(".pdf")) return true;
  } catch {
    if (/\.pdf(\?|#|$)/i.test(u)) return true;
  }
  return false;
}

function previewSnippet(value, max = 120) {
  if (value == null) return "";
  if (typeof value === "string") return value.slice(0, max);
  if (value instanceof ArrayBuffer || ArrayBuffer.isView(value)) {
    const len = value.byteLength ?? value.length ?? 0;
    return `[binary ${len} bytes]`;
  }
  return String(value).slice(0, max);
}

function decodeBase64PdfToUint8Array(input) {
  const raw = String(input || "").trim();
  const b64 = raw.replace(PDF_DATA_URL_RE, "").replace(/\s/g, "");
  if (!b64 || b64.length < 16) return null;
  try {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
    if (!bytesLookLikePdf(out)) return null;
    return out;
  } catch {
    return null;
  }
}

/**
 * SSOT: resolve how to render organized-tour itinerary / route narrative on profile.
 *
 * @param {{
 *   contentText?: string|null,
 *   contentHtmlSanitized?: string|null,
 *   resourceUrl?: string|null,
 *   resourceKind?: string|null,
 *   sourcePath?: string,
 * }} input
 */
export function resolveOrganizedTourItineraryContent(input = {}) {
  const sourcePath = String(input.sourcePath || "unknown");
  const contentText =
    typeof input.contentText === "string" ? input.contentText.trim() : "";
  const contentHtmlSanitized =
    typeof input.contentHtmlSanitized === "string" ? input.contentHtmlSanitized.trim() : "";
  const resourceUrl =
    typeof input.resourceUrl === "string" ? input.resourceUrl.trim() : "";
  const resourceKind =
    typeof input.resourceKind === "string" ? input.resourceKind.trim().toLowerCase() : "";

  let contentTypeDetected = "empty";
  let mimeType = null;
  let bytesLength = 0;
  let renderMode = "none";
  let embedUrl = null;
  let pdfDownloadUrl = null;
  let pdfBytes = null;
  let sanitizedHtml = null;
  let plainText = null;

  if (isHttpUrl(resourceUrl)) {
    pdfDownloadUrl = resourceUrl;
    if (urlLooksLikePdf(resourceUrl) || resourceKind === "pdf") {
      contentTypeDetected = "url_pdf";
      mimeType = "application/pdf";
      embedUrl = resourceUrl;
      renderMode = "embed_url";
    } else {
      contentTypeDetected = "url_unknown";
      mimeType = null;
    }
  }

  const b64FromText = contentText ? decodeBase64PdfToUint8Array(contentText) : null;
  const b64FromHtml = !b64FromText && contentHtmlSanitized
    ? decodeBase64PdfToUint8Array(contentHtmlSanitized)
    : null;
  const pdfFromB64 = b64FromText || b64FromHtml;

  if (pdfFromB64) {
    contentTypeDetected = "base64_pdf";
    mimeType = "application/pdf";
    bytesLength = pdfFromB64.byteLength;
    pdfBytes = pdfFromB64;
    renderMode = "pdf_blob";
    if (!pdfDownloadUrl) pdfDownloadUrl = resourceUrl || null;
  } else if (
    renderMode === "none" &&
    (contentLooksLikePdfSource(contentText) ||
      contentLooksLikePdfSource(contentHtmlSanitized))
  ) {
    contentTypeDetected = "pdf_binary_text";
    mimeType = "application/pdf";
    bytesLength = Math.max(contentText.length, contentHtmlSanitized.length);
    renderMode = "pdf_download_fallback";
    if (!pdfDownloadUrl && isHttpUrl(resourceUrl)) pdfDownloadUrl = resourceUrl;
  }

  if (renderMode === "none" || renderMode === "pdf_download_fallback") {
    const disp = resolveOrgTourInfoDisplay({
      contentHtmlSanitized: contentHtmlSanitized || null,
      contentText: contentText || null,
    });

    if (disp.mode === "html" && renderMode !== "pdf_download_fallback") {
      const html = getOrgTourInfoDangerousInnerHtml(disp);
      if (html && !orgTourInfoContentLooksLikeRawSource(html) && !contentLooksLikePdfSource(html)) {
        contentTypeDetected = "html";
        mimeType = "text/html";
        bytesLength = html.length;
        sanitizedHtml = html;
        renderMode = "html_sanitized";
      }
    }

    if (renderMode === "none" && disp.mode === "text") {
      const text = getOrgTourInfoPlainTextForDisplay(disp);
      if (text && !orgTourInfoContentLooksLikeRawSource(text) && !contentLooksLikePdfSource(text)) {
        contentTypeDetected = "text";
        mimeType = "text/plain";
        bytesLength = text.length;
        plainText = text;
        renderMode = "text_plain";
      }
    }
  }

  if (renderMode === "embed_url" && !embedUrl && isHttpUrl(resourceUrl)) {
    embedUrl = resourceUrl;
  }

  const model = {
    sourcePath,
    contentTypeDetected,
    mimeType,
    bytesLength,
    renderMode,
    preview: previewSnippet(plainText || sanitizedHtml || contentText || resourceUrl),
    embedUrl,
    pdfDownloadUrl,
    pdfBytes,
    sanitizedHtml,
    plainText,
  };

  traceOrganizedTourItineraryRender(model);
  return model;
}

/** DEV-only structured trace for itinerary render resolution. */
export function traceOrganizedTourItineraryRender(model) {
  if (!import.meta.env?.DEV || !model) return;
  // eslint-disable-next-line no-console
  console.info("[ORG_TOUR_ITINERARY_RENDER_TRACE]", {
    sourcePath: model.sourcePath,
    contentTypeDetected: model.contentTypeDetected,
    mimeType: model.mimeType,
    bytesLength: model.bytesLength,
    renderMode: model.renderMode,
    preview: model.preview,
  });
}
